/**
 * VolunteerPortal.js (v2 — Mobile PWA)
 * Raider Depot — Volunteer Portal
 * Route: /volunteer
 *
 * Three tabs per ADR-001 v2 / UI Architecture Spec:
 *   1. Pick List  — view + print aggregate order for current cycle
 *   2. Pickup     — scan QR or enter token, record picked_up / not_picked_up
 *   3. Inventory  — record Kroger delivery or donation receipt
 *
 * Auth: Firebase Anonymous session scoped to cycle
 * All writes go via Cloud Run API (volunteer write scope per ADR-001 v2)
 */

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInAnonymously, signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import QRScanner from "./QRScanner";
import { formatCycleDate, normalizeCycleId } from "../utils/dateUtils";
import { collection, query, where, getDocs } from "firebase/firestore";

const NAVY  = "#1A3B6E";
const MID   = "#2C5FA8";
const LIGHT = "#E8F2FB";
const ORDER_API_URL = "/api/orders";

// ── Shared styles ────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F7FAFD",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    backgroundColor: NAVY, padding: "16px 20px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    position: "sticky", top: 0, zIndex: 50,
  },
  headerTitle: { color: "#FFF", fontSize: 18, fontWeight: 700, margin: 0 },
  headerSub: { color: "rgba(255,255,255,0.6)", fontSize: 12, margin: "2px 0 0" },
  endBtn: {
    background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.3)",
    color: "#FFF", padding: "8px 14px", borderRadius: 8, fontSize: 13,
    fontWeight: 600, cursor: "pointer",
  },
  tabs: {
    display: "flex", backgroundColor: "#FFF",
    borderBottom: "1.5px solid #e2e8f0",
    position: "sticky", top: 60, zIndex: 40,
  },
  tab: {
    flex: 1, padding: "14px 4px", fontSize: 13, fontWeight: 500,
    color: "#64748b", border: "none", background: "none",
    cursor: "pointer", borderBottom: "3px solid transparent",
  },
  tabActive: { color: NAVY, borderBottomColor: NAVY, fontWeight: 700 },
  content: { padding: "16px 16px 80px" },
  card: {
    backgroundColor: "#FFF", borderRadius: 14,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "18px 16px", marginBottom: 14,
  },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: NAVY, margin: "0 0 12px" },
  input: {
    width: "100%", padding: "14px 16px", fontSize: 16,
    border: "1.5px solid #e2e8f0", borderRadius: 10,
    boxSizing: "border-box", outline: "none",
    fontFamily: "inherit", marginBottom: 10,
    WebkitAppearance: "none",
  },
  btnPrimary: {
    width: "100%", padding: "15px", fontSize: 15,
    fontWeight: 700, color: "#FFF", backgroundColor: NAVY,
    border: "none", borderRadius: 10, cursor: "pointer",
    minHeight: 52, WebkitAppearance: "none",
  },
  btnSecondary: {
    width: "100%", padding: "14px", fontSize: 14,
    fontWeight: 600, color: NAVY, backgroundColor: LIGHT,
    border: `1.5px solid ${MID}`, borderRadius: 10,
    cursor: "pointer", minHeight: 52, marginBottom: 8,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    WebkitAppearance: "none",
  },
  btnDisabled: { backgroundColor: "#94a3b8", cursor: "not-allowed" },
  statusBadge: (color) => ({
    display: "inline-block", padding: "3px 10px",
    borderRadius: 20, fontSize: 12, fontWeight: 600,
    backgroundColor: color + "22", color: color,
  }),
  // Pickup action buttons — large for easy tapping
  pickupBtn: {
    flex: 1, padding: "14px 8px", fontSize: 14, fontWeight: 700,
    border: "none", borderRadius: 10, cursor: "pointer",
    minHeight: 52, WebkitAppearance: "none",
  },
  loginContainer: {
    minHeight: "100vh", backgroundColor: "#F7FAFD",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20,
  },
  loginCard: {
    backgroundColor: "#FFF", borderRadius: 16, padding: "32px 24px",
    maxWidth: 400, width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)", textAlign: "center",
  },
};

// ── Pick List Tab ────────────────────────────────────────────────────────────
function PickListTab({ cycleId, cycles }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [printPanelId, setPrintPanelId] = useState(null);
  const [printBagCount, setPrintBagCount] = useState(1);

  useEffect(() => {
    if (!cycleId) return;
    setLoading(true);
    setError("");
    fetch(`${ORDER_API_URL}/picklist?cycleId=${cycleId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        return res.json();
      })
      .then((data) => setOrders(data.bags || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [cycleId]);

  const STATUS_COLORS = {
    submitted: "#3b82f6", packing: "#6366f1", packed: "#8b5cf6",
    flagged: "#f59e0b", picked_up: "#16a34a", cancelled: "#94a3b8",
  };

  const progressCounts = { submitted: 0, packing: 0, packed: 0, flagged: 0, picked_up: 0, cancelled: 0 };
  orders.forEach((o) => {
    if (!o.status) {
      console.warn("PickListTab: order returned from /picklist with no status", o.orderId || o.tokenId);
      return;
    }
    if (o.status in progressCounts) progressCounts[o.status]++;
  });

  const fmtInfoDate = (raw) => {
    if (!raw) return "—";
    const iso = raw?.toDate
      ? raw.toDate().toISOString()
      : raw?.seconds
      ? new Date(raw.seconds * 1000).toISOString()
      : raw;
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const printSheet = (order, bagCount = 1) => {
    const cycle = cycles.find((c) => c.cycleId === cycleId);
    const fmtDateTime = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
      });
    };
    const fmtFullDate = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
      });
    };
    const rawPickup = cycle?.pickupDate || cycle?.cycleId;
    const pickupIso = rawPickup?.toDate
      ? rawPickup.toDate().toISOString()
      : rawPickup?.seconds
      ? new Date(rawPickup.seconds * 1000).toISOString()
      : rawPickup;
    const pickupStr = fmtFullDate(pickupIso);
    const packedStatuses = ["packed", "ready_for_pickup", "picked_up", "not_picked_up", "returned_to_inventory", "fulfilled"];
    const packedStr = packedStatuses.includes(order.status) && order.statusUpdatedAt
      ? fmtDateTime(order.statusUpdatedAt) : "—";
    const todayStr = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const itemRows = (order.items || [])
      .map((item) => `<tr>
        <td style="padding:8px 10px;border:1px solid #e2e8f0;font-size:14px;">${item.name || item.item}</td>
        <td style="padding:8px 10px;border:1px solid #e2e8f0;font-size:14px;text-align:center;font-weight:600;">${item.qty}</td>
      </tr>`).join("");
    const renderSheet = (bagNum) => `
<div style="page-break-after:${bagNum < bagCount ? "always" : "auto"};break-after:${bagNum < bagCount ? "page" : "auto"};padding:0.5in;font-family:Arial,sans-serif;max-width:8.5in;box-sizing:border-box;">
  <div style="display:flex;justify-content:space-between;align-items:baseline;padding-bottom:12px;border-bottom:2px solid #1A3B6E;margin-bottom:20px;">
    <span style="font-size:28px;font-weight:700;color:#1A3B6E;">RAIDER DEPOT</span>
    <span style="font-size:15px;color:#374151;">Pickup: ${pickupStr}</span>
  </div>
  <div style="background:#EFF6FF;border:2px solid #1E3A5F;border-radius:10px;text-align:center;padding:20px 24px;margin-bottom:20px;">
    <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px;">Your Order Token</div>
    <div style="font-size:64px;font-weight:700;color:#1A3B6E;letter-spacing:3px;line-height:1.1;">${order.tokenId}</div>
    ${bagCount > 1 ? `<div style="font-size:16px;color:#374151;margin-top:8px;">Bag ${bagNum} of ${bagCount}</div>` : ""}
  </div>
  <div style="background:#f8fafc;border-radius:8px;padding:14px 16px;margin-bottom:20px;">
    <table style="width:100%;border-collapse:collapse;"><tbody>
      <tr><td style="font-size:13px;color:#6b7280;padding:4px 0;width:40%;">Order placed</td><td style="font-size:13px;color:#1e293b;padding:4px 0;font-weight:500;">${fmtDateTime(order.submittedAt)}</td></tr>
      <tr><td style="font-size:13px;color:#6b7280;padding:4px 0;">Cycle</td><td style="font-size:13px;color:#1e293b;padding:4px 0;font-weight:500;">${cycleId}</td></tr>
      <tr><td style="font-size:13px;color:#6b7280;padding:4px 0;">Packed</td><td style="font-size:13px;color:#1e293b;padding:4px 0;font-weight:500;">${packedStr}</td></tr>
      <tr><td style="font-size:13px;color:#6b7280;padding:4px 0;">Scheduled pickup</td><td style="font-size:13px;color:#1e293b;padding:4px 0;font-weight:500;">${pickupStr}</td></tr>
    </tbody></table>
  </div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
    <thead><tr>
      <th style="background:#F0FDF4;padding:8px 10px;border:1px solid #e2e8f0;text-align:left;font-size:13px;color:#374151;">Item</th>
      <th style="background:#F0FDF4;padding:8px 10px;border:1px solid #e2e8f0;text-align:center;font-size:13px;color:#374151;width:80px;">Qty</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <div style="background:#FEF3C7;border-radius:8px;padding:14px 16px;margin-bottom:20px;">
    <div style="font-weight:700;color:#92400e;font-size:14px;margin-bottom:6px;">Please return your empty bags!</div>
    <div style="font-size:13px;color:#78350f;line-height:1.5;">When you pick up your next order, please bring back any empty shopping bags from this order. Returned bags help us serve more families. Thank you!</div>
  </div>
  <div style="margin-bottom:20px;">
    <div style="font-size:13px;color:#374151;padding:6px 0;border-bottom:1px solid #f1f5f9;"><strong>Register your household:</strong> Visit riverwoodraiderdepot.org and scan your QR card to register and personalize your orders.</div>
    <div style="font-size:13px;color:#374151;padding:6px 0;border-bottom:1px solid #f1f5f9;"><strong>Share feedback:</strong> Let us know how we&rsquo;re doing at riverwoodraiderdepot.org/feedback</div>
    <div style="font-size:13px;color:#374151;padding:6px 0;"><strong>Need help?</strong> Contact your school counselor or visit the main office for assistance.</div>
  </div>
  <div style="border-top:1px solid #e2e8f0;padding-top:10px;display:flex;justify-content:space-between;align-items:center;">
    <span style="font-size:11px;color:#94a3b8;">Riverwood International Charter School &mdash; Raider Depot</span>
    <span style="font-size:11px;color:#94a3b8;">Printed ${todayStr}</span>
  </div>
</div>`;
    const sheets = Array.from({ length: bagCount }, (_, i) => renderSheet(i + 1)).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Pickup Sheet — ${order.tokenId}</title><style>@page{size:letter portrait;margin:0}*{box-sizing:border-box}body{margin:0;padding:0}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>${sheets}</body></html>`;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const printAllSubmitted = () => {
    const submitted = orders.filter((o) => (o.status || "submitted") === "submitted");
    if (submitted.length === 0) return;
    const cycle = cycles.find((c) => c.cycleId === cycleId);
    const fmtFullDate = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    };
    const rawPickup = cycle?.pickupDate || cycle?.cycleId;
    const pickupIso = rawPickup?.toDate
      ? rawPickup.toDate().toISOString()
      : rawPickup?.seconds
      ? new Date(rawPickup.seconds * 1000).toISOString()
      : rawPickup;
    const pickupStr = fmtFullDate(pickupIso);
    const todayStr = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const orderSections = submitted.map((order) => {
      const itemRows = (order.items || [])
        .map((item) => `<tr>
          <td style="padding:6px 10px;border:1px solid #e2e8f0;font-size:13px;">${item.name || item.item}</td>
          <td style="padding:6px 10px;border:1px solid #e2e8f0;font-size:13px;text-align:center;font-weight:600;">${item.qty}</td>
        </tr>`).join("");
      return `
<div style="page-break-inside:avoid;break-inside:avoid;margin-bottom:20px;padding-bottom:16px;border-bottom:1px solid #e2e8f0;">
  <div style="display:flex;align-items:center;gap:12px;margin-bottom:10px;">
    <span style="font-size:36px;font-weight:700;color:#1A3B6E;letter-spacing:2px;">${order.tokenId}</span>
    <span style="background:#dbeafe;color:#1d4ed8;font-size:11px;font-weight:700;padding:3px 8px;border-radius:4px;letter-spacing:0.5px;">SUBMITTED</span>
  </div>
  <table style="width:100%;border-collapse:collapse;">
    <thead><tr>
      <th style="background:#f8fafc;padding:6px 10px;border:1px solid #e2e8f0;text-align:left;font-size:12px;color:#374151;">Item</th>
      <th style="background:#f8fafc;padding:6px 10px;border:1px solid #e2e8f0;text-align:center;font-size:12px;color:#374151;width:70px;">Qty</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
</div>`;
    }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Pack List — ${cycleId}</title><style>@page{size:letter portrait;margin:0.5in}*{box-sizing:border-box}body{margin:0;padding:0;font-family:Arial,sans-serif}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
  <div style="padding-bottom:12px;border-bottom:2px solid #1A3B6E;margin-bottom:6px;display:flex;justify-content:space-between;align-items:baseline;">
    <span style="font-size:22px;font-weight:700;color:#1A3B6E;">RAIDER DEPOT &mdash; Pack List</span>
    <span style="font-size:14px;color:#374151;">Pickup: ${pickupStr}</span>
  </div>
  <div style="font-size:12px;color:#94a3b8;margin-bottom:24px;">Cycle: ${cycleId} &middot; ${submitted.length} order${submitted.length !== 1 ? "s" : ""} to pack &middot; Printed ${todayStr}</div>
  ${orderSections}
</body></html>`;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  if (loading) {
    return <div style={{ ...S.card, textAlign: "center", color: "#64748b", padding: "40px 20px" }}>Loading pick list...</div>;
  }

  if (error) {
    return <div style={{ ...S.card, textAlign: "center", color: "#ef4444", padding: "40px 20px" }}>{error}</div>;
  }

  const cycle = cycles.find((c) => c.cycleId === cycleId);
  const submittedCount = orders.filter((o) => (o.status || "submitted") === "submitted").length;

  return (
    <div>
      {/* Cycle info bar */}
      {cycle && (
        <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 16, marginBottom: 14 }}>
          {[
            { label: "Cycle",        value: cycle.cycleId },
            { label: "Orders open",  value: fmtInfoDate(cycle.openDate) },
            { label: "Orders close", value: fmtInfoDate(cycle.closeDate) },
            { label: "Pickup",       value: fmtInfoDate(cycle.pickupDate) },
          ].map(({ label, value }) => (
            <div key={label} style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Fulfillment progress */}
      {orders.length > 0 && (
        <div style={S.card}>
          <p style={S.sectionTitle}>Fulfillment progress</p>
          <div style={{ display: "flex", height: 28, borderRadius: 6, overflow: "hidden", background: "#f1f5f9", marginBottom: 16 }}>
            {[
              { key: "submitted", label: "Awaiting Pack", color: "#3b82f6" },
              { key: "packing",   label: "Packing",       color: "#6366f1" },
              { key: "packed",    label: "Packed",        color: "#8b5cf6" },
              { key: "flagged",   label: "Flagged",       color: "#f59e0b" },
              { key: "picked_up", label: "Picked Up",     color: "#16a34a" },
              { key: "cancelled", label: "Cancelled",     color: "#94a3b8" },
            ].map(({ key, label, color }) => {
              const count = progressCounts[key];
              if (count === 0) return null;
              const pct = (count / orders.length) * 100;
              return (
                <div key={key} title={`${label}: ${count}`}
                  style={{ width: `${pct}%`, background: color, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {pct >= 8 && <span style={{ fontSize: 11, color: "#fff", fontWeight: 600 }}>{count}</span>}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { label: "Awaiting Pack", key: "submitted", color: "#3b82f6", bg: "#eff6ff" },
              { label: "Packing",       key: "packing",   color: "#6366f1", bg: "#eef2ff" },
              { label: "Packed",        key: "packed",    color: "#8b5cf6", bg: "#f5f3ff" },
              { label: "Flagged",       key: "flagged",   color: "#f59e0b", bg: "#fffbeb" },
              { label: "Picked Up",     key: "picked_up", color: "#16a34a", bg: "#f0fdf4" },
              { label: "Cancelled",     key: "cancelled", color: "#94a3b8", bg: "#f8fafc" },
            ].map(({ label, key, color, bg }) => (
              <div key={key} style={{ background: bg, border: `1px solid ${color}33`, borderRadius: 8, padding: "10px 16px", minWidth: 90, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color }}>{progressCounts[key]}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Print Pack List */}
      {orders.length > 0 && (
        <button
          style={{ ...S.btnPrimary, marginBottom: 14, ...(submittedCount === 0 ? S.btnDisabled : {}) }}
          onClick={printAllSubmitted}
          disabled={submittedCount === 0}
        >
          Print Pack List ({submittedCount} submitted)
        </button>
      )}

      {/* Order cards */}
      {orders.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#64748b", padding: "40px 20px" }}>
          No orders found for this cycle.
        </div>
      ) : (
        orders.map((order) => {
          const borderColor = STATUS_COLORS[order.status || "submitted"] || "#3b82f6";
          return (
            <div key={order.tokenId} style={{ backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", borderLeft: `4px solid ${borderColor}`, marginBottom: 14 }}>
              <div style={{ padding: "10px 14px" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: NAVY, letterSpacing: "1.5px", lineHeight: 1, marginBottom: 10 }}>
                  {order.tokenId}
                </div>
                <div style={{ marginBottom: 10 }}>
                  {(order.items || []).map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 19, color: "#475569", padding: "2px 0" }}>
                      <span>{item.name || item.item}</span>
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>×{item.qty}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 13, color: "#94a3b8", borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                  {order.submittedAt && (
                    <span>Submitted{" "}
                      {new Date(order.submittedAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                      })}
                    </span>
                  )}
                </div>
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 8, marginTop: 8 }}>
                  {printPanelId === order.tokenId ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, color: "#64748b" }}>Bags:</span>
                      <button
                        style={{ width: 36, height: 36, borderRadius: 8, border: "1.5px solid #cbd5e1", background: "#FFF", fontSize: 20, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY }}
                        onClick={() => setPrintBagCount(Math.max(1, (parseInt(printBagCount) || 1) - 1))}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="10"
                        value={printBagCount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "") { setPrintBagCount(""); return; }
                          const n = parseInt(v, 10);
                          if (!isNaN(n)) setPrintBagCount(Math.max(1, Math.min(10, n)));
                        }}
                        onBlur={() => { if (printBagCount === "" || printBagCount < 1) setPrintBagCount(1); }}
                        style={{ width: 56, padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14, textAlign: "center" }}
                      />
                      <button
                        style={{ width: 36, height: 36, borderRadius: 8, border: "1.5px solid #cbd5e1", background: "#FFF", fontSize: 20, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY }}
                        onClick={() => setPrintBagCount(Math.min(10, (parseInt(printBagCount) || 1) + 1))}
                      >
                        +
                      </button>
                      <button
                        style={{ ...S.btnPrimary, width: "auto", minHeight: "auto", padding: "6px 14px", fontSize: 13 }}
                        onClick={() => { printSheet(order, parseInt(printBagCount) || 1); setPrintPanelId(null); setPrintBagCount(1); }}
                      >
                        Print
                      </button>
                      <button
                        style={{ ...S.btnSecondary, width: "auto", minHeight: "auto", padding: "6px 10px", fontSize: 13, marginBottom: 0 }}
                        onClick={() => { setPrintPanelId(null); setPrintBagCount(1); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      style={{ ...S.btnSecondary, padding: "6px 12px", fontSize: 13, marginBottom: 0 }}
                      onClick={() => { setPrintPanelId(order.tokenId); setPrintBagCount(1); }}
                    >
                      Print Pickup Sheet
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Pickup Tab ───────────────────────────────────────────────────────────────
function PickupTab({ cycleId }) {
  const [mode, setMode] = useState("input"); // input | scanning | confirming | done | error
  const [tokenInput, setTokenInput] = useState("");
  const [resolvedToken, setResolvedToken] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [recording, setRecording] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [recentActions, setRecentActions] = useState([]);

  const handleQRScan = useCallback((token) => {
    setShowScanner(false);
    setResolvedToken(token);
    setTokenInput(token);
    setMode("confirming");
  }, []);

  const handleManualEntry = () => {
    const t = tokenInput.trim().toUpperCase();
    if (!t) return;
    setResolvedToken(t);
    setMode("confirming");
  };

  const recordPickup = async (status) => {
    setRecording(true);
    setErrorMsg("");
    try {
      // Look up order by token + cycle to get orderId
      const res = await fetch(`${ORDER_API_URL}/${resolvedToken}`);
      if (!res.ok) throw new Error("Token not found");
      const orders = await res.json();
      const cycleOrder = orders.find((o) => o.cycleId === cycleId);
      if (!cycleOrder) throw new Error(`No order found for token ${resolvedToken} in cycle ${cycleId}. Try again.`);

      // Update status
      const updateRes = await fetch(`${ORDER_API_URL}/${cycleOrder.orderId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!updateRes.ok) throw new Error("Failed to update order status");

      setRecentActions((prev) => [
        { token: resolvedToken, status, time: new Date().toLocaleTimeString() },
        ...prev.slice(0, 9), // keep last 10
      ]);
      setResult({ token: resolvedToken, status });
      setMode("done");
    } catch (err) {
      setErrorMsg(err.message);
      setMode("error");
    } finally {
      setRecording(false);
    }
  };

  const reset = () => {
    setTokenInput("");
    setResolvedToken("");
    setResult(null);
    setErrorMsg("");
    setMode("input");
    setShowScanner(false);
  };

  return (
    <div>
      {/* Input / scan mode */}
      {mode === "input" && (
        <div style={S.card}>
          <p style={S.sectionTitle}>Record client pickup</p>
          <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px" }}>
            Scan a client's QR code or enter their token manually to record pickup or return.
          </p>

          {showScanner ? (
            <div style={{ marginBottom: 14 }}>
              <QRScanner
                active={showScanner}
                onScan={handleQRScan}
                onError={() => setShowScanner(false)}
                onClose={() => setShowScanner(false)}
              />
            </div>
          ) : (
            <button style={S.btnSecondary} onClick={() => setShowScanner(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MID} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3M17 17h3v3M14 20h3"/>
              </svg>
              Scan QR code
            </button>
          )}

          <div style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", margin: "4px 0 8px" }}>— or enter manually —</div>

          <input
            style={{ ...S.input, textTransform: "uppercase", letterSpacing: 2, fontSize: 18, fontWeight: 700, textAlign: "center" }}
            type="text"
            placeholder="RD-RW-001"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleManualEntry()}
            autoCapitalize="characters"
            autoComplete="off"
          />
          <button
            style={{ ...S.btnPrimary, ...(tokenInput.length < 6 ? S.btnDisabled : {}) }}
            onClick={handleManualEntry}
            disabled={tokenInput.length < 6}
          >
            Look up order →
          </button>
        </div>
      )}

      {/* Confirm mode — big pickup / return buttons */}
      {mode === "confirming" && (
        <div style={S.card}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>Recording for</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: NAVY, letterSpacing: 2 }}>{resolvedToken}</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Cycle: {formatCycleDate(cycleId)}</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              style={{ ...S.pickupBtn, backgroundColor: "#16A34A", color: "#FFF", opacity: recording ? 0.7 : 1 }}
              onClick={() => recordPickup("picked_up")}
              disabled={recording}
            >
              {recording ? "..." : "✓ Picked up"}
            </button>
            <button
              style={{ ...S.pickupBtn, backgroundColor: "#DC2626", color: "#FFF", opacity: recording ? 0.7 : 1 }}
              onClick={() => recordPickup("not_picked_up")}
              disabled={recording}
            >
              {recording ? "..." : "✗ Not picked up"}
            </button>
          </div>
          <button onClick={reset} style={{ width: "100%", marginTop: 10, padding: 12, fontSize: 13, color: "#64748b", background: "none", border: "1px solid #e2e8f0", borderRadius: 10, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      )}

      {/* Result mode */}
      {mode === "done" && result && (
        <div style={S.card}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>
              {result.status === "picked_up" ? "✅" : "📦"}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: result.status === "picked_up" ? "#16A34A" : "#DC2626" }}>
              {result.status === "picked_up" ? "Pickup recorded" : "Return recorded"}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: NAVY, marginTop: 6, letterSpacing: 2 }}>
              {result.token}
            </div>
          </div>
          <button style={S.btnPrimary} onClick={reset}>
            Next client →
          </button>
        </div>
      )}

      {/* Error mode */}
      {mode === "error" && (
        <div style={S.card}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, color: "#DC2626", marginBottom: 12 }}>{errorMsg}</div>
          </div>
          <button style={S.btnPrimary} onClick={reset}>Try again</button>
        </div>
      )}

      {/* Recent actions log */}
      {recentActions.length > 0 && (
        <div style={S.card}>
          <p style={{ ...S.sectionTitle, marginBottom: 10 }}>Recent actions</p>
          {recentActions.map((action, idx) => (
            <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: NAVY, letterSpacing: 1 }}>{action.token}</span>
              <span style={S.statusBadge(action.status === "picked_up" ? "#16A34A" : "#DC2626")}>
                {action.status === "picked_up" ? "Picked up" : "Returned"}
              </span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>{action.time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inventory Tab ────────────────────────────────────────────────────────────
function InventoryTab({ cycleId }) {
  const [source, setSource] = useState("kroger"); // kroger | donation
  const [items, setItems] = useState([{ name: "", qty: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const addItem = () => setItems((prev) => [...prev, { name: "", qty: "" }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) =>
    setItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

  const handleSubmit = async () => {
    const validItems = items.filter((i) => i.name.trim() && parseInt(i.qty) !== 0 && !isNaN(parseInt(i.qty)));
    if (validItems.length === 0) {
      setError("Please add at least one item with a name and quantity.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/inventory/receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source,
          cycleId,
          items: validItems.map((i) => ({
            name: i.name.trim(),
            qty: parseInt(i.qty),
          })),
          recordedAt: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setItems([{ name: "", qty: "" }]);
    setSubmitted(false);
    setError("");
    setSource("kroger");
  };

  if (submitted) {
    return (
      <div style={S.card}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#16A34A", marginBottom: 8 }}>
            Inventory recorded
          </div>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>
            {source === "kroger" ? "Kroger delivery" : "Donation"} recorded for cycle {formatCycleDate(cycleId)}.
          </div>
          <button style={S.btnPrimary} onClick={reset}>Record another</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={S.card}>
        <p style={S.sectionTitle}>Record inventory receipt</p>
        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
          Record items received from a Kroger delivery or community donation.
        </p>

        {/* Source selector */}
        <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
          {["kroger", "donation"].map((s) => (
            <button key={s} onClick={() => setSource(s)}
              style={{
                flex: 1, padding: "12px", fontSize: 14, fontWeight: 600,
                borderRadius: 10, cursor: "pointer", minHeight: 48,
                border: `2px solid ${source === s ? NAVY : "#e2e8f0"}`,
                backgroundColor: source === s ? LIGHT : "#FFF",
                color: source === s ? NAVY : "#64748b",
              }}>
              {s === "kroger" ? "Kroger delivery" : "Donation"}
            </button>
          ))}
        </div>

        {/* Items */}
        {items.map((item, idx) => (
          <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input
              style={{ ...S.input, flex: 3, marginBottom: 0 }}
              type="text"
              placeholder="Item name"
              value={item.name}
              onChange={(e) => updateItem(idx, "name", e.target.value)}
            />
            <input
              style={{ ...S.input, flex: 1, marginBottom: 0, textAlign: "center" }}
              type="number"
              placeholder="Qty"
              value={item.qty}
              onChange={(e) => updateItem(idx, "qty", e.target.value)}
              inputMode="decimal"
            />
            {items.length > 1 && (
              <button onClick={() => removeItem(idx)}
                style={{ width: 44, height: 44, borderRadius: 10, border: "1.5px solid #e2e8f0", background: "#FFF", fontSize: 20, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                ×
              </button>
            )}
          </div>
        ))}

        <button onClick={addItem}
          style={{ width: "100%", padding: "12px", fontSize: 14, color: MID, background: "none", border: `1.5px dashed ${MID}`, borderRadius: 10, cursor: "pointer", marginBottom: 14, marginTop: 4 }}>
          + Add another item
        </button>

        {error && <p style={{ color: "#ef4444", fontSize: 13, marginBottom: 10 }}>{error}</p>}

        <button
          style={{ ...S.btnPrimary, ...(submitting ? S.btnDisabled : {}) }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Recording..." : `Record ${source === "kroger" ? "Kroger delivery" : "donation"}`}
        </button>
      </div>
    </div>
  );
}

// ── Main Volunteer Portal ────────────────────────────────────────────────────
export default function VolunteerPortal() {
  const [step, setStep] = useState("login"); // login | portal | error
  const [loading, setLoading] = useState(false);
  const [cycleId, setCycleId] = useState(""); // canonical YYYY-MM-DD used in API
  const [cycleInput, setCycleInput] = useState(""); // user input with MM/DD/YYYY format shown
  const [errorMsg, setErrorMsg] = useState("");
  const [activeTab, setActiveTab] = useState("picklist");
  const [availableCycles, setAvailableCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch available cycles on component mount
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        setCyclesLoading(true);
        const q = query(collection(db, 'cycles'), where('cycleStatus', '==', 'Active'));
        const querySnapshot = await getDocs(q);
        const cycles = querySnapshot.docs.map(doc => ({ ...doc.data(), cycleId: doc.id }));
        setAvailableCycles(cycles);
        
        // Auto-populate with the fulfillment cycle:
        // Prefer the open cycle where today <= pickupDate (earliest upcoming pickup).
        // If no cycle has a future pickupDate, fall back to the latest cycleId.
        if (cycles.length > 0) {
          const now = new Date();

          const upcomingOpen = cycles
            .filter(c => c.cycleStatus === 'Active' && c.orderStatus === 'Open' && new Date(c.pickupDate) >= now)
            .sort((a, b) => new Date(a.pickupDate) - new Date(b.pickupDate));

          const anyOpen = cycles
            .filter(c => c.cycleStatus === 'Active' && c.orderStatus === 'Open')
            .sort((a, b) => new Date(b.openDate) - new Date(a.openDate));

          // Final fallback: most recent Active cycle (may be Active+Closed — packing in progress)
          const mostRecent = [...cycles]
            .sort((a, b) => new Date(b.openDate) - new Date(a.openDate));

          const defaultCycle = upcomingOpen[0] ?? anyOpen[0] ?? mostRecent[0];
          setCycleId(defaultCycle.cycleId);
          setCycleInput(formatCycleDate(defaultCycle.cycleId));
        }
      } catch (err) {
        console.error('Failed to fetch cycles:', err);
      } finally {
        setCyclesLoading(false);
      }
    };
    
    fetchCycles();
  }, []);

  const handleStart = async () => {
    if (!cycleInput.trim()) return;

    const normalized = normalizeCycleId(cycleInput);
    if (!normalized) {
      setErrorMsg(`No active cycle found for ${cycleInput}. Please check with your coordinator.`);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    const cycleExists = availableCycles.some(c => c.cycleId === normalized);
    if (!cycleExists) {
      setErrorMsg(`No active cycle found for ${cycleInput}. Please check with your coordinator.`);
      setLoading(false);
      return;
    }

    try {
      setCycleId(normalized);
      setCycleInput(formatCycleDate(normalized)); // keep pretty value in input for UX
      await signInAnonymously(auth);
      setStep("portal");
    } catch (err) {
      setErrorMsg(err.message);
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const handleExit = async () => {
    await signOut(auth);
    setStep("login");
    setCycleId("");
    setCycleInput("");
    setActiveTab("picklist");
  };

  // ── Login screen ─────────────────────────────────────────────────────
  if (step === "login") {
    return (
      <div style={S.loginContainer}>
        <div style={S.loginCard}>
          <div style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: LIGHT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
              <rect x="4" y="18" width="32" height="18" rx="2" fill={NAVY}/>
              <polygon points="20,4 2,20 38,20" fill={NAVY}/>
              <rect x="15" y="24" width="10" height="12" fill={LIGHT}/>
            </svg>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: "0 0 6px" }}>Volunteer portal</h1>
          <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, margin: "0 0 22px" }}>
            Enter the cycle ID to start your session. Your counselor coordinator will provide this.
          </p>

          {errorMsg && (
            <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>{errorMsg}</p>
          )}

          {cyclesLoading ? (
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>Loading active cycles...</p>
          ) : (
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
              Enter cycle ID (MM/DD/YYYY, MM-DD-YYYY, or MMDDYYYY). Auto-filled active cycle if available.
            </p>
          )}

          <input
            style={{ ...S.input, fontSize: 17, textAlign: "center", fontWeight: 600 }}
            type="text"
            placeholder="MM/DD/YYYY"
            value={cycleInput}
            onChange={(e) => setCycleInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStart()}
            autoComplete="off"
          />

          <button
            style={{ ...S.btnPrimary, ...((!cycleId.trim() || loading) ? S.btnDisabled : {}) }}
            onClick={handleStart}
            disabled={!cycleId.trim() || loading}
          >
            {loading ? "Starting session..." : "Start volunteer session"}
          </button>
        </div>
      </div>
    );
  }

  // ── Error screen ─────────────────────────────────────────────────────
  if (step === "error") {
    return (
      <div style={S.loginContainer}>
        <div style={S.loginCard}>
          <p style={{ fontSize: 14, color: "#ef4444", marginBottom: 16 }}>{errorMsg}</p>
          <button style={S.btnPrimary} onClick={() => setStep("login")}>Try again</button>
        </div>
      </div>
    );
  }

  // ── Portal ────────────────────────────────────────────────────────────
  const TABS = [
    { id: "packing",   label: "Pack Orders" },
    { id: "picklist",  label: "Pick list" },
    { id: "pickup",    label: "Pickup" },
    { id: "inventory", label: "Inventory" },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.headerTitle}>Volunteer portal</h1>
          <p style={S.headerSub}>Cycle: {formatCycleDate(cycleId)}</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button style={S.endBtn} onClick={() => navigate("/")}>🏠 Home</button>
          <button style={S.endBtn} onClick={handleExit}>End session</button>
        </div>
      </div>

      <div style={S.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            style={{ ...S.tab, ...(activeTab === tab.id ? S.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={S.content}>
        {activeTab === "picklist"  && <PickListTab  cycleId={cycleId} cycles={availableCycles} />}
        {activeTab === "pickup"    && <PickupTab    cycleId={cycleId} />}
        {activeTab === "inventory" && <InventoryTab cycleId={cycleId} />}
        {activeTab === "packing" && (
          <div style={S.card}>
            <p style={S.sectionTitle}>Pack Orders</p>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 16 }}>Use the dedicated packing portal to claim and pack individual orders at your own pace.</p>
            <button style={S.btnPrimary} onClick={() => navigate("/volunteer/packing")}>Open Packing Portal</button>
          </div>
        )}
      </div>
    </div>
  );
}
